import { useState, useEffect } from 'react';
import IngredientList from './components/IngredientList';
import IngredientForm from './components/IngredientForm';
import RecipeList from './components/RecipeList';
import RecipeEditor from './components/RecipeEditor';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';
import './i18n';
import { AppProvider, useAppContext } from './context/AppContext';

const AppContent = () => {
  const [refresh, setRefresh] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const { t } = useTranslation();
  const { user, login, logout, language, changeLanguage, loading, api } = useAppContext();

  // Set default language from browser if not set
  useEffect(() => {
    if (!language || language === 'cimode') {
      const browserLang = navigator.language?.slice(0, 2) || 'en';
      if (['en', 'es', 'fr', 'it', 'pt'].includes(browserLang)) {
        changeLanguage(browserLang);
      } else {
        changeLanguage('en');
      }
    }
  }, []);

  const reload = () => setRefresh(r => !r);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEditRecipe = (recipe) => {
    setEditingRecipe(recipe);
    setActiveTab(2);
  };

  const handleNewRecipe = () => {
    setEditingRecipe(null);
    setActiveTab(2);
  };

  const handleCloneRecipe = (recipe) => {
    const { id, status, ...rest } = recipe;
    setEditingRecipe({ ...rest, status: 'draft', cloneFrom: id });
    setActiveTab(2);
  };

  // Google login handler
  const handleGoogleLogin = () => {
    window.open(
      process.env.REACT_APP_API_BASE_URL + '/auth/google/redirect',
      'googleLogin',
      'width=500,height=600'
    );
    window.addEventListener('message', async function receiveUser(event) {
      // Verificar que el mensaje venga del dominio de la API
      const apiUrl = new URL(process.env.REACT_APP_API_BASE_URL);
      const apiOrigin = apiUrl.origin;
      
      if (event.origin !== apiOrigin) return;
      if (event.data && event.data.type === 'google-auth-success') {
        login(event.data.user, event.data.token);
        window.removeEventListener('message', receiveUser);
      }
    });
  };

  if (loading) return null; // TODO: loader/spinner

  return (
    <Box sx={{ width: '100%', maxWidth: '1600px', mx: 'auto', mt: 1 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Select
          value={language}
          onChange={e => changeLanguage(e.target.value)}
          size="small"
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="en">English</MenuItem>
          <MenuItem value="es">Español</MenuItem>
          <MenuItem value="fr">Français</MenuItem>
          <MenuItem value="it">Italiano</MenuItem>
          <MenuItem value="pt">Português</MenuItem>
        </Select>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <img src="/icon.svg" alt="Ice Cream Calculator" style={{ width: '48px', height: '48px' }} />
          <Box
            component="h1"
            sx={{
              margin: 0,
              fontSize: '1.6rem',
              color: '#667eea',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Ice Cream Calculator
          </Box>
        </Box>
        <Box>
          {user ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{user.name}</span>
              <Button size="small" onClick={logout}>{t('logout')}</Button>
            </Box>
          ) : (
            <Button variant="outlined" onClick={handleGoogleLogin}>
              {t('login')}
            </Button>
          )}
        </Box>
      </Box>
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label={t('recipes')} />
        <Tab label={t('ingredients')} />
        <Tab label={t('recipe_editor')} />
      </Tabs>
      {activeTab === 0 && (
        <>
          <Button
            variant="contained"
            color="primary"
            sx={{ mb: 2 }}
            onClick={handleNewRecipe}
          >
            {t('new_recipe')}
          </Button>
          <RecipeList
            key={refresh}
            onEdit={handleEditRecipe}
            onClone={handleCloneRecipe}
            api={api}
          />
        </>
      )}
      {activeTab === 1 && (
        <>
          <IngredientForm onCreated={reload} api={api} />
          <IngredientList key={refresh} api={api} />
        </>
      )}
      {activeTab === 2 && (
        <RecipeEditor
          recipe={editingRecipe}
          onSaved={() => {
            reload();
          }}
          api={api}
        />
      )}
    </Box>
  );
};

const App = () => (
  <AppProvider>
    <AppContent />
  </AppProvider>
);

export default App;